'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ReferralSystemProps {
  referralCode: string;
  referralCount: number;
  referralRewards: {
    milestone: number;
    reward: string;
    achieved: boolean;
  }[];
}

const ReferralSystem: React.FC<ReferralSystemProps> = ({
  referralCode,
  referralCount,
  referralRewards,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(referralCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const referralLink = `https://noenemy.life/join?ref=${referralCode}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-card rounded-lg p-6 border border-border">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
          <Icon name="UserGroupIcon" size={24} className="text-accent" />
        </div>
        <div>
          <h3 className="text-xl font-headline font-bold text-foreground">
            Spread the Movement
          </h3>
          <p className="text-sm text-muted-foreground">
            Invite others and earn recognition
          </p>
        </div>
      </div>

      <div className="bg-background rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm text-muted-foreground mb-1">Your Referral Code</div>
            <div className="text-2xl font-bold text-primary">{referralCode}</div>
          </div>
          <button
            onClick={handleCopyCode}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium transition-colors hover:bg-primary/90"
          >
            {copied ? 'Copied!' : 'Copy Code'}
          </button>
        </div>

        <div className="pt-3 border-t border-border">
          <div className="text-sm text-muted-foreground mb-2">Share Your Link</div>
          <div className="flex items-center space-x-2">
            <input
              type="text"
              value={referralLink}
              readOnly
              className="flex-1 px-3 py-2 bg-card border border-input rounded-md text-sm text-foreground"
            />
            <button
              onClick={handleCopyLink}
              className="p-2 bg-muted hover:bg-muted/80 rounded-md transition-colors"
              aria-label="Copy link"
            >
              <Icon name="ClipboardDocumentIcon" size={20} className="text-foreground" />
            </button>
          </div>
        </div>
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-foreground">Your Impact</h4>
          <div className="text-2xl font-bold text-accent">{referralCount}</div>
        </div>
        <p className="text-sm text-muted-foreground">
          People joined through your invitation
        </p>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-foreground mb-4">Referral Rewards</h4>
        <div className="space-y-3">
          {referralRewards.map((reward, index) => (
            <div
              key={index}
              className={`flex items-center justify-between p-3 rounded-lg ${
                reward.achieved ? 'bg-success/10' : 'bg-muted'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Icon
                  name={reward.achieved ? 'CheckCircleIcon' : 'LockClosedIcon'}
                  size={20}
                  className={reward.achieved ? 'text-success' : 'text-muted-foreground'}
                />
                <div>
                  <div className="text-sm font-medium text-foreground">
                    {reward.milestone} Referrals
                  </div>
                  <div className="text-xs text-muted-foreground">{reward.reward}</div>
                </div>
              </div>
              {reward.achieved && (
                <span className="text-xs font-medium text-success">Unlocked!</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="InformationCircleIcon" size={16} />
          <span>Share on social media to reach more people</span>
        </div>
        <div className="flex items-center space-x-3 mt-3">
          <button className="p-2 bg-muted hover:bg-muted/80 rounded-full transition-colors">
            <Icon name="ShareIcon" size={20} className="text-foreground" />
          </button>
          <span className="text-sm text-muted-foreground">
            Facebook, Twitter, LinkedIn, WhatsApp
          </span>
        </div>
      </div>
    </div>
  );
};

export default ReferralSystem;